import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGames } from '@/Context/GameContext';
import { ArrowLeft } from 'lucide-react';
import { useScreenSize } from '@/hooks';
import styles from './Game.module.css';

/**
 * Game Route Component
 * Displays game in iframe at route /providername/gameid
 * 
 * Route Pattern: /:providername/:gameid
 * 
 * Examples:
 * - /spribe/crash-game-123 → GIS game from Spribe provider
 * - /esports/4ee8e0051a035b463b47c3c473ce317d → E-sports (JS game)
 * - /pragmaticplay/slot-game-456 → GIS game from Pragmatic Play
 * 
 * How it works:
 * 1. User clicks game → handleGameLaunch() or handleJsGameLaunch() called
 * 2. Game API returns game URL → stored in GameContext state
 * 3. navigate(`/${providerName}/${gameId}`) → navigates here
 * 4. useParams() extracts { providername, gameid } from URL
 * 5. Component displays iframe with game URL from GameContext
 * 6. If page refreshed → reloads game using route params
 */
const Game = () => {
  // Extract route parameters from URL: /providername/gameid
  const { providername, gameid } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  
  // Get game state from GameContext (iframeUrl, showIframe, etc.)
  const { 
    iframeUrl,        // Game iframe URL (set by handleGameLaunch/handleJsGameLaunch)
    showIframe,       // Whether to show iframe
    showIframeLoading, // Loading state
    currentGame,       // Current game info { provider, gameId, title, url }
    handleGameLaunch, // Launch GIS games
    handleJsGameLaunch, // Launch JS games (e-sports)
    closeGame         // Close game and navigate back
  } = useGames();
  
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isReloading, setIsReloading] = useState(false);

  /**
   * Handle direct navigation to game route (page refresh or direct URL access)
   * 
   * This effect runs when:
   * - User refreshes page while on /providername/gameid
   * - User directly navigates to /providername/gameid URL
   * 
   * It reloads the game using the route parameters (providername, gameid)
   */
  useEffect(() => {
    // Don't reload if showIframe is false (game is being closed)
    if (!showIframe) {
      return;
    }
    
    // Check if we need to reload the game (page refresh scenario)
    // Needs reload if: route params exist but no iframe URL yet
    const needsReload = providername && gameid && !iframeUrl && !showIframeLoading && !isReloading;
    
    // Also check if route params don't match current game (different game)
    // Route mismatch if: provider or gameId changed
    const routeMismatch = providername && gameid && 
      (currentGame?.provider !== providername.toLowerCase() || currentGame?.gameId !== gameid);
    
    if (needsReload || routeMismatch) {
      setIsReloading(true);
      
      // Check if it's an e-sports/sportsbook (JS game) or GIS game
      const lowerProvider = providername.toLowerCase();
      const isJsGame = lowerProvider === 'esports' || lowerProvider === 'sportsbook';

      if (isJsGame) {
        // Reload JS game (e-sports/sportsbook) using handleJsGameLaunch
        const jsProvider = lowerProvider === 'sportsbook' ? 'SPORTSBOOK' : 'ESPORTS';
        handleJsGameLaunch(gameid, navigate, jsProvider)
          .then(() => {
            setIsReloading(false);
          })
          .catch((error) => {
            console.error('Failed to reload JS game:', error);
            setIsReloading(false);
          });
      } else {
        // Create game object from route params to reload GIS game
        // Example: /spribe/crash-game-123
        const gameObject = {
          uuid: gameid,
          id: gameid,
          provider: providername.toLowerCase(),
          vendor: providername.toLowerCase(),
          title: `${providername} Game`,
          name: `${providername} Game`
        };
        
        // Reload the GIS game using handleGameLaunch
        handleGameLaunch(gameObject, navigate)
          .then(() => {
            setIsReloading(false);
          })
          .catch((error) => {
            console.error('Failed to reload game:', error);
            setIsReloading(false);
          });
      }
    }
  }, [providername, gameid, iframeUrl, showIframeLoading, isReloading, currentGame, handleGameLaunch, handleJsGameLaunch, navigate, showIframe]);

  // Handle iframe load
  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  // Reset iframe loaded state when iframeUrl changes (reload scenario)
  useEffect(() => {
    if (iframeUrl) {
      setIframeLoaded(false);
    }
  }, [iframeUrl]);

  // Handle back button
  const handleBack = () => {
    closeGame(navigate);
  };

  // Get provider display name
  const getProviderName = () => {
    if (currentGame?.provider) {
      return currentGame.provider.charAt(0).toUpperCase() + currentGame.provider.slice(1);
    }
    if (providername) {
      return providername.charAt(0).toUpperCase() + providername.slice(1);
    }
    return 'Game';
  };

  // Show loading if no iframe URL or if we're reloading
  if (!showIframe || !iframeUrl || isReloading) {
    return (
      <div className={styles.gameContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.gameContainer}>
      {/* Mobile Header - Only show provider name and back button */}
      {isMobile && (
        <div className={styles.mobileHeader}>
          <button 
            className={styles.backButton}
            onClick={handleBack}
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className={styles.providerName}>{getProviderName()}</h2>
        </div>
      )}

      {/* Loading Overlay */}
      {showIframeLoading && !iframeLoaded && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
          <p>Loading game...</p>
        </div>
      )}

      {/* Game Iframe */}
      <iframe
        key={iframeUrl} // Force reload when URL changes
        src={iframeUrl}
        className={styles.gameIframe}
        onLoad={handleIframeLoad}
        allowFullScreen
        title={currentGame?.title || 'Game'}
        style={{
          position: isMobile ? 'fixed' : 'static',
          top: isMobile ? '50px' : '0',
          left: isMobile ? '0' : undefined,
          width: '100%',
          height: isMobile ? 'calc(100vh - 50px)' : '100vh'
        }}
      />
    </div>
  );
};

export default Game;
